import React, { useState } from "react";
import axios from "axios";
import { MdCheckCircle, MdEmail } from "react-icons/md";
import AlertPane from "components/alert";
import Button from "components/button";
import Dialog from "components/dialog";
import { InputField, Textarea } from "components/fields";
import { getLinkHubTheme } from "../theme";

const BASE_URL = process.env.REACT_APP_BASE_URL || "http://localhost:8080";
const FORM_ID = "link-hub-visitor-form";

const EMPTY_FORM = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  message: "",
};

const VisitorForm = ({ isOpen, onClose, slug, profile, onTrackEvent }) => {
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const theme = getLinkHubTheme(profile);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      const response = await axios.post(
        `${BASE_URL}/api/connect/public/${slug}/visitor`,
        formData
      );

      if (response.data.success) {
        setSuccess(true);
        if (onTrackEvent) {
          onTrackEvent("visitor_form_submit");
        }
        setFormData(EMPTY_FORM);

        // Close after the thank-you message has been visible for a moment.
        setTimeout(() => {
          setSuccess(false);
          onClose();
        }, 2000);
      }
    } catch (err) {
      console.error("Error submitting visitor form:", err);
      setError(err.response?.data?.message || "Failed to submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        <span className="flex items-center gap-2">
          <MdEmail aria-hidden="true" className="h-6 w-6 shrink-0" style={{ color: theme.accent }} />
          <span style={{ fontFamily: theme.headingFont, color: theme.heading }}>Contact Us</span>
        </span>
      }
      footer={
        success ? null : (
          <>
            <Button
              variant="secondary"
              onClick={onClose}
              style={{ borderRadius: theme.radius, fontFamily: theme.bodyFont }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form={FORM_ID}
              loading={submitting}
              style={theme.primaryButtonStyle}
            >
              {submitting ? "Submitting…" : "Submit"}
            </Button>
          </>
        )
      }
    >
      {success ? (
        <div className="py-8 text-center" style={{ fontFamily: theme.bodyFont }}>
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-success-subtle text-success-fg">
            <MdCheckCircle aria-hidden="true" className="h-10 w-10" />
          </div>
          <h3 className="mb-2 text-heading">Thank You!</h3>
          <p className="text-sm text-fg-muted">
            We've received your message and will get back to you soon.
          </p>
        </div>
      ) : (
        <form
          id={FORM_ID}
          onSubmit={handleSubmit}
          className="space-y-4"
          style={{ fontFamily: theme.bodyFont }}
        >
          {error && (
            <AlertPane tone="danger" className="">
              {error}
            </AlertPane>
          )}
          <InputField
            label="First Name"
            name="firstName"
            autoComplete="given-name"
            value={formData.firstName}
            onChange={handleInputChange}
            required
          />
          <InputField
            label="Last Name"
            name="lastName"
            autoComplete="family-name"
            value={formData.lastName}
            onChange={handleInputChange}
            required
          />
          <InputField
            label="Email"
            type="email"
            name="email"
            autoComplete="email"
            value={formData.email}
            onChange={handleInputChange}
            required
          />
          <InputField
            label="Phone"
            type="tel"
            name="phone"
            autoComplete="tel"
            value={formData.phone}
            onChange={handleInputChange}
          />
          <Textarea
            label="Message"
            name="message"
            value={formData.message}
            onChange={handleInputChange}
            rows={4}
          />
        </form>
      )}
    </Dialog>
  );
};

export default VisitorForm;
